import React, { useCallback, useState } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import AdminMenu from '../components/AdminMenu';

/**
 * Admin → Network Reachability page.
 *
 * Lets an admin probe whether Lyric's pod can reach a candidate upstream —
 * an EKS API server, an on-prem Spectrocloud-managed cluster, an internal
 * HTTP service, etc. Used during onboarding (e.g. workflow-tracker → §0 P1
 * in docs/WORKFLOW-TRACKER-ON-LYRIC.md) so we can answer "can the pod even
 * reach the box?" without `kubectl exec`.
 *
 * The probe ladder is L4 → L5 (TLS) → L7 (HTTP) depending on the chosen
 * scheme. See server/routes/netcheck.js for the wire shape.
 *
 * Curated suggestions for the most common workflow-tracker use cases are
 * included so an operator can prefill plausible targets in one click.
 */

const PRESETS = [
    {
        label: 'EKS API server (us-west-2)',
        host: 'eks.us-west-2.amazonaws.com',
        port: 443,
        scheme: 'https',
        hint: 'EKS public regional endpoint. Confirms outbound to AWS; per-cluster API hostnames are different (eks describe-cluster returns the real one).',
    },
    {
        label: 'AWS STS (for IRSA token exchange)',
        host: 'sts.us-west-2.amazonaws.com',
        port: 443,
        scheme: 'https',
        hint: 'Used by boto3 to mint EKS bearer tokens via the GetCallerIdentity trick.',
    },
    {
        label: 'AWS Secrets Manager (us-west-2)',
        host: 'secretsmanager.us-west-2.amazonaws.com',
        port: 443,
        scheme: 'https',
        hint: 'Where tools/lyric/* secrets live. Confirms the secret-rotation watcher can poll.',
    },
    {
        label: 'Spectrocloud API',
        host: 'api.spectrocloud.com',
        port: 443,
        scheme: 'https',
        hint: 'workflow-tracker uses this to download kubeconfigs for managed clusters.',
    },
];

const SCHEMES = [
    { value: 'tcp',   label: 'TCP — connect only' },
    { value: 'tls',   label: 'TLS — TCP + handshake' },
    { value: 'http',  label: 'HTTP — TCP + GET /' },
    { value: 'https', label: 'HTTPS — TLS + GET /' },
];

const emptyTarget = () => ({
    host: '',
    port: 443,
    scheme: 'https',
    // Auth configuration for the L7 GET / probe. `mode` is one of:
    //   'none'   – no headers (default)
    //   'bearer' – Authorization: Bearer <token>
    //   'custom' – arbitrary { name, value } header pairs
    // Stored on the row (not global) because different targets need different
    // credentials (e.g. EKS bearer token vs. internal API key).
    auth: { mode: 'none', bearer: '', custom: [{ name: '', value: '' }] },
    // Whether the auth panel is expanded in the UI. Doesn't go to the server.
    _authOpen: false,
});

/**
 * Convert a row's auth config into the header object the server expects.
 * Returns null when there is nothing meaningful to send so the row's request
 * body omits the `headers` key entirely.
 */
function buildHeaders(auth) {
    if (!auth || auth.mode === 'none') return null;
    if (auth.mode === 'bearer') {
        const tok = (auth.bearer || '').trim();
        return tok ? { Authorization: `Bearer ${tok}` } : null;
    }
    if (auth.mode === 'custom') {
        const out = {};
        for (const { name, value } of auth.custom || []) {
            const n = (name || '').trim();
            const v = (value || '').trim();
            if (n && v) out[n] = v;
        }
        return Object.keys(out).length ? out : null;
    }
    return null;
}

export default function AdminNetCheck() {
    const { oktaAuth } = useOktaAuth() || {};
    const [targets, setTargets] = useState([emptyTarget()]);
    const [timeoutMs, setTimeoutMs] = useState(5000);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [errorDetail, setErrorDetail] = useState(null);
    const [results, setResults] = useState(null);
    const [meta, setMeta] = useState(null);

    const setTargetField = (idx, field, value) => {
        setTargets((curr) => curr.map((t, i) => (i === idx ? { ...t, [field]: value } : t)));
    };
    const setTargetAuth = (idx, patch) => {
        setTargets((curr) => curr.map((t, i) => (
            i === idx ? { ...t, auth: { ...t.auth, ...patch } } : t
        )));
    };
    const setCustomHeader = (idx, headerIdx, field, value) => {
        setTargets((curr) => curr.map((t, i) => {
            if (i !== idx) return t;
            const next = t.auth.custom.map((h, hi) => (hi === headerIdx ? { ...h, [field]: value } : h));
            return { ...t, auth: { ...t.auth, custom: next } };
        }));
    };
    const addCustomHeader = (idx) => setTargets((curr) => curr.map((t, i) => (
        i === idx ? { ...t, auth: { ...t.auth, custom: [...t.auth.custom, { name: '', value: '' }] } } : t
    )));
    const removeCustomHeader = (idx, headerIdx) => setTargets((curr) => curr.map((t, i) => {
        if (i !== idx) return t;
        const next = t.auth.custom.filter((_, hi) => hi !== headerIdx);
        return { ...t, auth: { ...t.auth, custom: next.length ? next : [{ name: '', value: '' }] } };
    }));

    const addTarget = () => setTargets((curr) => [...curr, emptyTarget()]);
    const removeTarget = (idx) => setTargets((curr) => (curr.length === 1 ? curr : curr.filter((_, i) => i !== idx)));
    const addPreset = (preset) => {
        const { host, port, scheme } = preset;
        // If the first row is blank, fill it; otherwise append a new row.
        setTargets((curr) => {
            const firstBlank = curr.findIndex((t) => !t.host);
            if (firstBlank >= 0) {
                return curr.map((t, i) => (i === firstBlank ? { ...t, host, port, scheme } : t));
            }
            return [...curr, { ...emptyTarget(), host, port, scheme }];
        });
    };

    const authHeader = useCallback(async () => {
        let token = '';
        try { token = oktaAuth ? await oktaAuth.getAccessToken() : ''; } catch { /* dev */ }
        return token ? { Authorization: `Bearer ${token}` } : {};
    }, [oktaAuth]);

    const runProbe = useCallback(async () => {
        // Client-side validation so we don't even send empty rows.
        const cleaned = targets
            .map((t) => {
                const host = String(t.host || '').trim();
                const port = Number(t.port);
                const scheme = t.scheme || 'tcp';
                const headers = (scheme === 'http' || scheme === 'https') ? buildHeaders(t.auth) : null;
                const row = { host, port, scheme };
                if (headers) row.headers = headers;
                return row;
            })
            .filter((t) => t.host && Number.isInteger(t.port) && t.port >= 1 && t.port <= 65535);
        if (cleaned.length === 0) {
            setError('At least one target with host + port is required.');
            return;
        }
        setLoading(true);
        setError('');
        setErrorDetail(null);
        setResults(null);
        setMeta(null);
        try {
            const headers = { ...(await authHeader()), 'Content-Type': 'application/json' };
            const res = await fetch('/api/admin/netcheck', {
                method: 'POST',
                headers,
                body: JSON.stringify({ targets: cleaned, timeoutMs }),
            });
            const body = await res.json().catch(() => ({}));
            if (!res.ok) {
                // Preserve the full server body so the UI can show the
                // "Admin only" diagnostic (currentUser, reason, hint) instead
                // of just a useless "Admin only" string.
                setErrorDetail({ status: res.status, ...body });
                throw new Error(body?.error || `HTTP ${res.status}`);
            }
            setResults(body.results);
            setMeta(body.meta);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [targets, timeoutMs, authHeader]);

    return (
        <div style={styles.page}>
            <div style={styles.pageHeader}>
                <h2 style={styles.title}>🌐 Network Reachability</h2>
                <AdminMenu />
            </div>
            <p style={styles.subtitle}>
                Probe whether <strong>Lyric&apos;s pod</strong> can reach a target host:port. Use this
                before onboarding a new tool — it answers &ldquo;can the pod even open a socket
                to this thing?&rdquo; without needing <code>kubectl exec</code>. Read-only:
                opens a connection, optionally a TLS handshake, optionally an HTTP GET. Never
                writes anything.
            </p>

            {/* ── Suggestions ────────────────────────────────────────────── */}
            <section style={styles.section}>
                <h3 style={styles.h3}>Quick suggestions</h3>
                <p style={styles.help}>One click to prefill a common target. Most useful for the
                    workflow-tracker §0 P1 check (see <code>docs/WORKFLOW-TRACKER-ON-LYRIC.md</code>).
                </p>
                <div style={styles.presetRow}>
                    {PRESETS.map((p) => (
                        <button key={p.label} type="button" onClick={() => addPreset(p)}
                            style={styles.presetBtn} title={p.hint}>
                            + {p.label}
                        </button>
                    ))}
                </div>
            </section>

            {/* ── Targets ────────────────────────────────────────────────── */}
            <section style={styles.section}>
                <h3 style={styles.h3}>Targets</h3>
                <table style={styles.table}>
                    <thead>
                        <tr>
                            <th style={styles.th}>Host</th>
                            <th style={{ ...styles.th, width: '110px' }}>Port</th>
                            <th style={{ ...styles.th, width: '220px' }}>Scheme</th>
                            <th style={{ ...styles.th, width: '110px' }}>Auth</th>
                            <th style={{ ...styles.th, width: '60px' }}></th>
                        </tr>
                    </thead>
                    <tbody>
                        {targets.map((t, idx) => {
                            const isL7 = t.scheme === 'http' || t.scheme === 'https';
                            const hasAuth = t.auth.mode !== 'none' && !!buildHeaders(t.auth);
                            return (
                                <React.Fragment key={idx}>
                                    <tr>
                                        <td style={styles.td}>
                                            <input
                                                type="text"
                                                value={t.host}
                                                onChange={(e) => setTargetField(idx, 'host', e.target.value)}
                                                placeholder="e.g. eks.us-west-2.amazonaws.com"
                                                style={styles.input}
                                            />
                                        </td>
                                        <td style={styles.td}>
                                            <input
                                                type="number"
                                                value={t.port}
                                                min={1}
                                                max={65535}
                                                onChange={(e) => setTargetField(idx, 'port', Number(e.target.value))}
                                                style={styles.input}
                                            />
                                        </td>
                                        <td style={styles.td}>
                                            <select
                                                value={t.scheme}
                                                onChange={(e) => setTargetField(idx, 'scheme', e.target.value)}
                                                style={styles.input}
                                            >
                                                {SCHEMES.map((s) => (
                                                    <option key={s.value} value={s.value}>{s.label}</option>
                                                ))}
                                            </select>
                                        </td>
                                        <td style={styles.td}>
                                            <button
                                                type="button"
                                                onClick={() => setTargetField(idx, '_authOpen', !t._authOpen)}
                                                disabled={!isL7}
                                                title={isL7 ? 'Configure Authorization header' : 'Auth headers only apply to HTTP/HTTPS probes'}
                                                style={isL7 ? styles.authBtn : styles.authBtnDisabled}
                                            >
                                                {hasAuth ? '🔒 Auth set' : '🔓 No auth'}
                                                <span style={{ marginLeft: 6, opacity: 0.7 }}>{t._authOpen ? '▾' : '▸'}</span>
                                            </button>
                                        </td>
                                        <td style={styles.td}>
                                            <button
                                                type="button"
                                                onClick={() => removeTarget(idx)}
                                                disabled={targets.length === 1}
                                                style={targets.length === 1 ? styles.btnDangerDisabled : styles.btnDanger}
                                                title="Remove row"
                                            >
                                                ✕
                                            </button>
                                        </td>
                                    </tr>
                                    {isL7 && t._authOpen && (
                                        <tr>
                                            <td colSpan={5} style={styles.authCell}>
                                                <div style={styles.authPanel}>
                                                    <div style={styles.authModeRow}>
                                                        <strong style={{ color: '#aaa', fontSize: '12px' }}>Authorization for GET /:</strong>
                                                        <label style={styles.radio}>
                                                            <input type="radio" name={`auth-${idx}`}
                                                                checked={t.auth.mode === 'none'}
                                                                onChange={() => setTargetAuth(idx, { mode: 'none' })} />
                                                            None
                                                        </label>
                                                        <label style={styles.radio}>
                                                            <input type="radio" name={`auth-${idx}`}
                                                                checked={t.auth.mode === 'bearer'}
                                                                onChange={() => setTargetAuth(idx, { mode: 'bearer' })} />
                                                            Bearer token
                                                        </label>
                                                        <label style={styles.radio}>
                                                            <input type="radio" name={`auth-${idx}`}
                                                                checked={t.auth.mode === 'custom'}
                                                                onChange={() => setTargetAuth(idx, { mode: 'custom' })} />
                                                            Custom header(s)
                                                        </label>
                                                    </div>

                                                    {t.auth.mode === 'bearer' && (
                                                        <div style={{ marginTop: '8px' }}>
                                                            <input
                                                                type="password"
                                                                value={t.auth.bearer}
                                                                onChange={(e) => setTargetAuth(idx, { bearer: e.target.value })}
                                                                placeholder="paste token (sent as: Authorization: Bearer …)"
                                                                style={styles.input}
                                                            />
                                                            <div style={styles.help}>
                                                                Token value is masked in results and never logged on the server.
                                                            </div>
                                                        </div>
                                                    )}

                                                    {t.auth.mode === 'custom' && (
                                                        <div style={{ marginTop: '8px' }}>
                                                            {t.auth.custom.map((h, hi) => (
                                                                <div key={hi} style={styles.customHeaderRow}>
                                                                    <input
                                                                        type="text"
                                                                        value={h.name}
                                                                        placeholder="Header name (e.g. X-Api-Key)"
                                                                        onChange={(e) => setCustomHeader(idx, hi, 'name', e.target.value)}
                                                                        style={{ ...styles.input, flex: '0 0 220px' }}
                                                                    />
                                                                    <input
                                                                        type="text"
                                                                        value={h.value}
                                                                        placeholder="Header value"
                                                                        onChange={(e) => setCustomHeader(idx, hi, 'value', e.target.value)}
                                                                        style={{ ...styles.input, flex: 1 }}
                                                                    />
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => removeCustomHeader(idx, hi)}
                                                                        style={styles.btnDanger}
                                                                        title="Remove header"
                                                                    >
                                                                        ✕
                                                                    </button>
                                                                </div>
                                                            ))}
                                                            <button type="button" onClick={() => addCustomHeader(idx)} style={styles.btn}>
                                                                + Add header
                                                            </button>
                                                            <div style={styles.help}>
                                                                Max {20} headers / 8 KB total. <code>Host</code>, <code>Content-Length</code>, and hop-by-hop headers are refused.
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </tbody>
                </table>
                <button onClick={addTarget} style={styles.btn}>+ Add target</button>

                <div style={styles.actions}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#ddd' }}>
                        <span>Timeout (ms)</span>
                        <input
                            type="number"
                            value={timeoutMs}
                            min={500}
                            max={30000}
                            step={500}
                            onChange={(e) => setTimeoutMs(Number(e.target.value))}
                            style={{ ...styles.input, width: '110px' }}
                        />
                    </label>
                    <button onClick={runProbe} disabled={loading} style={styles.btnPrimary}>
                        {loading ? '⏳ Probing…' : '🚀 Run probe'}
                    </button>
                </div>

                {error && (
                    <div style={styles.error}>
                        <div>⚠️ {error}</div>
                        {errorDetail && (
                            <div style={{ marginTop: '8px', fontSize: '12px', lineHeight: 1.5 }}>
                                {errorDetail.status === 403 ? (
                                    <>
                                        <div>The server identified you as: <code>{errorDetail.currentUser || 'unknown'}</code></div>
                                        {errorDetail.reason && <div>Reason: <code>{errorDetail.reason}</code></div>}
                                        {errorDetail.hint && <div style={{ marginTop: '4px', color: '#ffe082' }}>{errorDetail.hint}</div>}
                                        <div style={{ marginTop: '6px', color: '#aaa' }}>
                                            If you can see the <strong>Admin</strong> menu but this page rejects you,
                                            those two endpoints use slightly different paths — paste this whole error
                                            into the engineering chat so we can fix the gate.
                                        </div>
                                    </>
                                ) : (
                                    <pre style={{ margin: 0, fontSize: '11px', color: '#aaa', whiteSpace: 'pre-wrap' }}>
                                        {JSON.stringify(errorDetail, null, 2)}
                                    </pre>
                                )}
                            </div>
                        )}
                    </div>
                )}
            </section>

            {/* ── Results ────────────────────────────────────────────────── */}
            {results && (
                <section style={styles.section}>
                    <h3 style={styles.h3}>Results</h3>
                    {meta?.podIdentity && (
                        <p style={styles.help}>
                            Probed from pod <code>{meta.podIdentity.hostname}</code>
                            {meta.podIdentity.region ? <> (region <code>{meta.podIdentity.region}</code>)</> : null}
                            . Timeout used: <code>{meta.timeoutMs}ms</code>.
                        </p>
                    )}
                    <table style={styles.resultsTable}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Target</th>
                                <th style={{ ...styles.th, width: '90px' }}>Result</th>
                                <th style={{ ...styles.th, width: '100px' }}>Latency</th>
                                <th style={styles.th}>Detail</th>
                            </tr>
                        </thead>
                        <tbody>
                            {results.map((r, idx) => (
                                <tr key={idx} style={r.ok ? undefined : styles.rowFail}>
                                    <td style={styles.td}>
                                        <div style={{ fontWeight: 600 }}>{r.host}:{r.port}</div>
                                        <div style={{ color: '#888', fontSize: '11px' }}>{r.scheme}</div>
                                    </td>
                                    <td style={styles.td}>
                                        {r.ok ? (
                                            <span style={styles.badgeOk}>✅ OK</span>
                                        ) : (
                                            <span style={styles.badgeFail}>❌ FAIL</span>
                                        )}
                                    </td>
                                    <td style={styles.td}>{r.latencyMs}ms</td>
                                    <td style={styles.td}>
                                        {r.error && <div style={styles.errInline}>{r.error}</div>}
                                        {r.tls && (
                                            <div style={styles.tlsBlock}>
                                                <div><strong>TLS:</strong> {r.tls.protocol} {r.tls.authorized ? '(trusted)' : '(UNTRUSTED)'}</div>
                                                {r.tls.cert && (
                                                    <div style={{ color: '#aaa', fontSize: '11px', marginTop: '2px' }}>
                                                        <div>subject: <code>{r.tls.cert.subject}</code></div>
                                                        <div>issuer: <code>{r.tls.cert.issuer}</code></div>
                                                        <div>valid: {r.tls.cert.validFrom} → {r.tls.cert.validTo}</div>
                                                    </div>
                                                )}
                                                {r.tls.authorizationError && (
                                                    <div style={styles.errInline}>cert error: {r.tls.authorizationError}</div>
                                                )}
                                            </div>
                                        )}
                                        {r.http && (
                                            <div style={styles.httpBlock}>
                                                <div><strong>HTTP:</strong> {r.http.status}</div>
                                                {r.http.server && <div style={{ color: '#aaa', fontSize: '11px' }}>server: {r.http.server}</div>}
                                                {r.http.contentType && <div style={{ color: '#aaa', fontSize: '11px' }}>content-type: {r.http.contentType}</div>}
                                            </div>
                                        )}
                                        {Array.isArray(r.sentHeaders) && r.sentHeaders.length > 0 && (
                                            <div style={styles.sentHeadersBlock}>
                                                <div style={{ color: '#aaa', fontSize: '11px', marginBottom: '2px' }}>
                                                    <strong>Sent headers:</strong>
                                                </div>
                                                {r.sentHeaders.map(([n, v], hi) => (
                                                    <div key={hi} style={{ fontSize: '11px', color: '#bbb' }}>
                                                        <code>{n}</code>: {v}
                                                    </div>
                                                ))}
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <h4 style={styles.h4}>How to read this</h4>
                    <ul style={styles.legend}>
                        <li><strong>✅ OK + low latency:</strong> Lyric&apos;s pod can reach the host. You&apos;re unblocked on P1 for that target.</li>
                        <li><strong>❌ TCP: timed out:</strong> firewall / security group / routing problem. Talk to platform/networking.</li>
                        <li><strong>❌ TCP: ECONNREFUSED:</strong> reached the box, port not open. Wrong port, or service down.</li>
                        <li><strong>❌ TCP: ENOTFOUND:</strong> DNS doesn&apos;t resolve from inside the pod. May need a private hosted zone or VPC DNS.</li>
                        <li><strong>❌ TLS: cert error:</strong> reachable but TLS broken — wrong SNI, MITM, or self-signed CA not trusted.</li>
                        <li><strong>❌ HTTP: 4xx / 5xx:</strong> reachable, but the server doesn&apos;t like an anonymous GET /. That&apos;s usually fine — Lyric only needs TCP+TLS for k8s API access; HTTP status here is informational.</li>
                        <li><strong>🔒 HTTP: 401 / 403:</strong> reachable, server requires auth. Expand <em>Auth</em> on the target row to add a Bearer token or custom headers (e.g. <code>Authorization</code>, <code>X-API-Key</code>). The result row echoes back which headers were sent (secret values masked).</li>
                    </ul>
                </section>
            )}
        </div>
    );
}

const styles = {
    page: { padding: '24px', maxWidth: '1100px', margin: '0 auto', color: '#fff' },
    pageHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', marginBottom: '8px' },
    title: { margin: 0, color: '#4fc3f7' },
    subtitle: { color: '#aaa', marginTop: 0, lineHeight: 1.5 },
    section: { marginTop: '20px', padding: '16px', borderRadius: '6px', background: '#1f2429', border: '1px solid #2f3a44' },
    h3: { marginTop: 0, marginBottom: '8px', color: '#4fc3f7' },
    h4: { marginTop: '16px', marginBottom: '6px', color: '#aaa', fontSize: '0.9rem', textTransform: 'uppercase' },
    help: { color: '#888', fontSize: '0.85rem', marginTop: 0 },
    presetRow: { display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '8px' },
    presetBtn: {
        padding: '6px 10px', background: '#1e3a5f', color: '#cfe8ff',
        border: '1px solid #2a4a72', borderRadius: '4px', cursor: 'pointer', fontSize: '12px',
    },
    table: { width: '100%', borderCollapse: 'collapse', marginTop: '8px' },
    resultsTable: { width: '100%', borderCollapse: 'collapse', marginTop: '8px', fontSize: '13px' },
    th: { textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid #2f3a44', color: '#aaa', fontWeight: 600, fontSize: '0.85rem' },
    td: { padding: '6px 8px', verticalAlign: 'top', borderBottom: '1px solid #232a31' },
    rowFail: { background: '#2a1a1a' },
    input: {
        padding: '6px 8px', borderRadius: '4px', border: '1px solid #444',
        background: '#161a1e', color: '#fff', width: '100%', boxSizing: 'border-box', fontSize: '13px',
    },
    btn: { marginTop: '8px', padding: '8px 14px', background: '#2f3a44', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' },
    btnPrimary: { padding: '8px 14px', background: '#0052cc', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontWeight: 600 },
    btnDanger: { padding: '6px 10px', background: '#5a1a1a', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer', fontSize: '0.85rem' },
    btnDangerDisabled: { padding: '6px 10px', background: '#2a2a2a', color: '#666', border: 'none', borderRadius: '4px', cursor: 'not-allowed', fontSize: '0.85rem' },
    actions: { display: 'flex', alignItems: 'center', gap: '12px', marginTop: '16px', flexWrap: 'wrap' },
    error: { padding: '8px 12px', background: '#4a1a1a', border: '1px solid #ef5350', borderRadius: '4px', color: '#ffcdd2', margin: '8px 0' },
    errInline: { color: '#ef9a9a', fontSize: '12px' },
    badgeOk: { display: 'inline-block', padding: '2px 8px', background: '#1a4a1a', color: '#c8e6c9', border: '1px solid #4caf50', borderRadius: '10px', fontSize: '11px', fontWeight: 600 },
    badgeFail: { display: 'inline-block', padding: '2px 8px', background: '#4a1a1a', color: '#ffcdd2', border: '1px solid #ef5350', borderRadius: '10px', fontSize: '11px', fontWeight: 600 },
    tlsBlock: { marginTop: '4px', padding: '6px 8px', background: '#0d1117', border: '1px solid #2f3a44', borderRadius: '4px', fontSize: '12px' },
    httpBlock: { marginTop: '4px', padding: '6px 8px', background: '#0d1117', border: '1px solid #2f3a44', borderRadius: '4px', fontSize: '12px' },
    sentHeadersBlock: { marginTop: '4px', padding: '6px 8px', background: '#0d1117', border: '1px dashed #2f3a44', borderRadius: '4px' },
    legend: { color: '#bbb', fontSize: '13px', lineHeight: 1.7 },
    authBtn: { padding: '6px 10px', background: '#1e3a5f', color: '#cfe8ff', border: '1px solid #2a4a72', borderRadius: '4px', cursor: 'pointer', fontSize: '12px', width: '100%' },
    authBtnDisabled: { padding: '6px 10px', background: '#2a2a2a', color: '#666', border: '1px solid #3a3a3a', borderRadius: '4px', cursor: 'not-allowed', fontSize: '12px', width: '100%' },
    authCell: { padding: '8px', borderBottom: '1px solid #232a31', background: '#161a1e' },
    authPanel: { padding: '10px', background: '#0d1117', border: '1px solid #2f3a44', borderRadius: '4px' },
    authModeRow: { display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' },
    radio: { display: 'flex', alignItems: 'center', gap: '4px', color: '#ddd', fontSize: '12px', cursor: 'pointer' },
    customHeaderRow: { display: 'flex', gap: '6px', marginBottom: '6px', alignItems: 'center' },
};
